"use client";

import { SELLER_PRICING } from "@/lib/constants/seller-pricing";
import EmailOrSms from "@/components/common/emailOrSms";
import { EmailVerificationDialog } from "@/components/email-verification/email-verification-dialog";
import { Button } from "@/components/rac/button";
import { Checkbox } from "@/components/rac/checkbox";
import { DialogContent, DialogOverlay } from "@/components/rac/dialog";
import { SheetContent, SheetHeader, SheetOverlay } from "@/components/rac/sheet";
import type { TemplateParamsInput, TemplateQueryInput } from "@/gql/getTemplate";
import { UNIT_ONE_QUERY } from "@/gql/unit";
import { useAuth } from "@/hooks/use-auth";
import { useIsMobile } from "@/hooks/use-mobile";
import { useUserStore } from "@/stores/user-store";
import { cn } from "@/utils/cn";
import getFeeParams from "@/utils/getFeeParams";
import { logger } from "@/utils/logger";
import { useMutation, useQuery } from "@apollo/client";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { ButtonActions } from "./action-buttons";
import { useFormContext } from "./context/FormProvider";
import { fetchRawUnit } from "./fetchRawUnit";
import { CREATE_UNIT_AND_CONFIRM_MUTATION } from "./hooks/useGraphQLQueries";
import { ProfileFormData } from "./step-1-profile";
import { ValidationPrerequisitesModal, validatePrerequisites } from "./validation-prerequisites-modal";
interface MouFormData {
  agreedToTerms: boolean;
  phone?: string;
  email?: string;
  otp?: string;
  isConfirmationDone: boolean;
}
interface MouWrapperProps {
  query?: TemplateQueryInput;
  templateParams?: TemplateParamsInput;
  onSubmit?: (data: MouFormData) => void;
  className?: string;
  onBack: () => void;
  onNext: () => void;
  isLoading?: boolean;
}
export function StepFeeConfirmation({
  query = {
    triggerHooks: "seller.confirmation.mailSent",
    type: "email"
  },
  templateParams = {
    body: {
      customer: "Customer",
      commission: {
        type: "p",
        value: 2,
        includesGst: true
      },
      examplePrice: "3 Crore"
    }
  },
  onNext,
  onBack,
  className,
  isLoading
}: MouWrapperProps) {
  const isMobile = useIsMobile();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [selectedButton, setSelectedButton] = useState<"review" | "agree" | null>(null);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [currentTemplateParams, setCurrentTemplateParams] = useState<TemplateParamsInput>(templateParams);
  const {
    user,
    isAuthenticated
  } = useAuth();
  const [createUnitAndConfirmFee] = useMutation(CREATE_UNIT_AND_CONFIRM_MUTATION);
  const {
    state,
    updateStepData
  } = useFormContext();

  // Validation modal state
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const contextProfile = state.profile as Partial<ProfileFormData>;

  // Get rawUnitId and unitId from URL params
  const groupId = searchParams?.get("groupId");
  const rawUnitId = searchParams?.get("rawUnitId");
  const currentSellerUnitId = searchParams?.get("sellerUnitId");

  // Get the selected group from user preferences store (priority over first group)
  const storeSelectedGroupId = useUserStore(state_0 => state_0.selectedGroupId);
  const sellerGroupId = storeSelectedGroupId || user?.groups?.find(group => group.relationship === "seller")?.id;

  // Fetch unit data using UNIT_ONE_QUERY if unitId exists
  const {
    loading,
    data: unitData = {}
  } = useQuery(UNIT_ONE_QUERY, {
    variables: {
      filters: {
        one: {
          sellerUnitId: currentSellerUnitId
        }
      }
    },
    skip: !isAuthenticated || Boolean(!currentSellerUnitId)
  });
  const unitPrice = unitData.units?.one?.salePrice || null;
  const confirmationStatus = unitData.units?.one?.confirmationStatus || "";
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState,
    setValue,
    ...useFormProps
  } = useForm({
    defaultValues: {
      agreedToTerms: false,
      otp: "",
      isConfirmationDone: false,
      phone: contextProfile?.phoneNumber || user?.phones?.[0]?.number || "",
      email: contextProfile?.email || user?.emails?.[0]?.address || ""
    },
    mode: "onChange"
  });
  const watchedValues = watch();

  // Effect to fetch unit data and update template params
  useEffect(() => {
    if (confirmationStatus === "confirmed") {
      setValue("isConfirmationDone", true, {
        shouldDirty: true
      });
      updateStepData("fee", {
        isConfirmationDone: true
      });
    } else {
      const fetchUnitDataAndUpdateParams = async () => {
        let price: number | null = null;
        if (unitPrice) {
          // Extract price from unit data
          price = unitPrice || 7500000;
        } else if (rawUnitId && sellerGroupId) {
          try {
            // Fetch raw unit data
            const rawUnit = await fetchRawUnit({
              groupId: sellerGroupId,
              id: rawUnitId
            });
            price = rawUnit?.salePrice || 7500000;
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Failed to fetch raw unit data:", error);
          }
        }
        // Update template params if we have a price
        if (price && price > 0) {
          const feeParams = getFeeParams({
            price
          });
          setCurrentTemplateParams(prev => ({
            ...prev,
            body: {
              customer: prev.body?.customer ?? "Customer",
              commission: {
                type: feeParams.commission.type as import("@/gql/getTemplate").CommissionType,
                value: feeParams.commission.value,
                min: feeParams.commission.min,
                includesGst: feeParams.commission.includesGst
              },
              examplePrice: feeParams.examplePrice
            }
          }));
        }
      };
      fetchUnitDataAndUpdateParams();
    }
  }, [currentSellerUnitId, updateStepData, setValue, unitPrice, confirmationStatus, rawUnitId, sellerGroupId]);
  const paramStepKey = searchParams?.get("process") || "fee";
  const handleFormSubmit = async (data: MouFormData) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      // Get rawUnitId from URL params
      const rawUnitId_0 = searchParams?.get("rawUnitId");
      const userId = user?.id;
      if (!userId) {
        throw new Error("User not authenticated");
      }

      // Call the mutation
      const {
        data: mutationData
      } = await createUnitAndConfirmFee({
        variables: {
          filters: {
            createUnitAndConfirmFee: {
              rawUnitId: rawUnitId_0,
              sellerUnitId: currentSellerUnitId,
              userId,
              device: {
                for: "email",
                id: data.email,
                mode: "mail",
                data: {
                  otp: data.otp
                }
              }
            }
          },
          // both rawUnitId and currentSellerUnitId are optional, so we skip only if both are missing
          skip: !isAuthenticated || !currentSellerUnitId && !rawUnitId_0
        }
      });
      const sellerUnitId = mutationData?.unitMethods?.createUnitAndConfirmFee?.sellerUnitId;
      if (sellerUnitId) {
        setValue("isConfirmationDone", true, {
          shouldDirty: true
        });
        // Update URL with sellerUnitId after successful confirmation
        router.replace(`${pathname}?sellerUnitId=${sellerUnitId}&groupId=${groupId}&process=${paramStepKey}`);
        return;
      }
      toast.error("Unable to confirm fee", {
        description: "Please try again or contact support."
      });
      logger.error("Error in createUnitAndConfirmFee: missing sellerUnitId");
    } catch (error_0) {
      toast.error("Unable to confirm fee", {
        description: "Please try again or contact support."
      });
      logger.error("Error in createUnitAndConfirmFee:", {
        data,
        error: error_0
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleEmailVerificationSuccess = async (email: string, otp: string) => {
    // Validate prerequisites before proceeding
    const errors = validatePrerequisites(state);
    if (errors.length > 0) {
      setValidationErrors(errors);
      setIsValidationModalOpen(true);
      return;
    }
    setValue("email", email, {
      shouldDirty: true
    });
    setValue("otp", otp, {
      shouldDirty: true
    });
    setIsEmailVerified(true);

    // Call handleFormSubmit with mutation
    await handleFormSubmit({
      agreedToTerms: watchedValues.agreedToTerms,
      email,
      otp,
      isConfirmationDone: false
    });
  };
  const handleAgree = () => {
    setSelectedButton(selectedButton === "agree" ? null : "agree");
    const newAgreedValue = !watchedValues.agreedToTerms;
    setValue("agreedToTerms", newAgreedValue, {
      shouldDirty: true
    });

    // Reset email verification when unchecking terms
    if (!newAgreedValue) {
      setIsEmailVerified(false);
    }
  };
  const handleSheetOpenChange = (isOpen: boolean) => {
    setIsSheetOpen(isOpen);
    if (!isOpen) {
      setSelectedButton(null);
    }
  };

  // Loading or Submitting state
  if (loading || isSubmitting) {
    return <div className="flex flex-col items-center justify-center min-h-[80vh] gap-4 p-4 text-center">
				<div className="relative">
					<svg className="animate-spin h-12 w-12 text-primary-600" height="24" viewBox="0 0 24 24" fill="none">
						<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
						<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
					</svg>
					<div className="absolute inset-0 flex items-center justify-center">
						<div className="w-2 h-2 bg-primary-600 rounded-full animate-ping" />
					</div>
				</div>
				<p className="text-neutral-600 font-medium animate-pulse">{loading ? "Loading" : "Submitting"} details...</p>
			</div>;
  }
  return <div className={cn("min-h-screen bg-gray-50", className)}>
			{/* Validation Prerequisites Modal */}
			<ValidationPrerequisitesModal isOpen={isValidationModalOpen} onOpenChange={setIsValidationModalOpen} validationErrors={validationErrors} />

			{/* Conditional rendering based on unit confirmation status */}
			{!watchedValues.isConfirmationDone ? <>
					<h1 className="text-base sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">Settlin | MOU – Summary</h1>

					{/* What we do for you */}
					<div className="mb-4">
						<h2 className="text-sm sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">What we do for you:</h2>
						<ul className="list-disc pl-4 sm:pl-5 space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-700">
							<li>Market your property across multiple platforms (Magicbricks, 99acres, etc.)</li>
							<li>Handle all buyer inquiries and visits (only genuine buyers)</li>
							<li>Manage follow-ups, negotiations, and documentation</li>
							<li>Support in legalities and loan approvals for smooth closure</li>
						</ul>
					</div>

					{/* Our Fee */}
					<div className="mb-4">
						<h2 className="text-lg font-semibold text-gray-900 mb-3">Our Fee:</h2>
						<div className="bg-blue-50 p-2 rounded-lg">
							{/* Fee percentage - stacked on mobile, inline on desktop */}
							<div className="mb-2">
								<div className="flex flex-wrap items-baseline gap-1">
									<span className="line-through text-gray-400 text-lg sm:text-xl">2.36%</span>
									<span className="text-2xl sm:text-3xl font-bold text-gray-800">{SELLER_PRICING.feePercentage}%</span>
									<span className="text-sm sm:text-base text-gray-600">of the sale value (inclusive GST)</span>
								</div>
							</div>
							<div className="text-xs sm:text-sm text-gray-500 mb-4">
								Subject to a minimum of 1 lakh and a maximum of 5 lakhs
							</div>
							<div className="mt-4 sm:mt-6 mb-4 sm:mb-6 w-full">
								<h4 className="font-semibold text-gray-900 mb-3 sm:mb-4 text-sm sm:text-base">Special Pricing:</h4>
								<div className="bg-white border border-blue-200 rounded-lg p-3 sm:p-4">
									<ul className="text-xs sm:text-sm text-gray-700 space-y-2">
										{SELLER_PRICING.specialPricing.map((item, index) => <li key={index} className="flex items-start">
												<span className="text-blue-600 mr-2 flex-shrink-0">•</span>
												<span>{item.label}</span>
											</li>)}
									</ul>
								</div>
							</div>
							<div className="mt-4 sm:mt-6 mb-4 sm:mb-6 w-full">
								<h4 className="font-semibold text-gray-900 mb-3 sm:mb-4 text-sm sm:text-base">Fee Examples:</h4>
								<div className="bg-white border border-blue-200 rounded-lg overflow-hidden">
									<table className="w-full">
										<thead>
											<tr className="bg-gray-100">
												<th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">
													Sale Price
												</th>
												<th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">
													Our Fee
												</th>
											</tr>
										</thead>
										<tbody className="divide-y divide-gray-100">
											{SELLER_PRICING.examples.map((example, index_0) => <tr key={index_0} className="hover:bg-gray-50 transition-colors">
													<td className="px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm text-gray-800">
														{example.salePriceLabel}
													</td>
													<td className="px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-semibold text-blue-600">
														{example.feeLabel}
													</td>
												</tr>)}
										</tbody>
									</table>
								</div>
							</div>
							<ul className="list-disc pl-4 sm:pl-5 space-y-2 text-xs sm:text-sm text-gray-700">
								<li>All above is inclusive GST. Payable only after the deal closes</li>
								<li>No hidden charges; any extra costs for extra services will require your consent</li>
							</ul>
						</div>
					</div>

					{/* Once you confirm */}
					<div className="mb-8">
						<h2 className="text-sm sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">Once you confirm:</h2>
						<ul className="list-disc pl-4 sm:pl-5 space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-700">
							<li>We&apos;ll visit your property for floorplan & photos</li>
							<li>A dedicated relationship manager will be assigned</li>
							<li>Buyer feedback & property review shared every 45 days</li>
							<li>Real time updates available via your login</li>
						</ul>
					</div>

					{/* Form */}
					<FormProvider {...{
        control,
        handleSubmit,
        reset,
        watch,
        formState,
        setValue,
        ...useFormProps
      }}>
						<form className="space-y-4 sm:space-y-6">
							{/* MOU Sheet Overlay */}
							{/* MOU Sheet/Dialog Overlay */}
							{isMobile ? <SheetOverlay isOpen={isSheetOpen} onOpenChange={handleSheetOpenChange}>
									<SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto rounded-t-2xl p-4" closeButton={true}>
										<SheetHeader>
											<h2 className="text-xl font-semibold text-gray-900">MOU</h2>
										</SheetHeader>

										{/* MOU Content - Scrollable */}
										<div className="flex-1 overflow-y-auto px-4 py-4">
											<EmailOrSms params={currentTemplateParams} query={query} showSubject={false} showSignature={false} className="prose prose-sm max-w-none 
											[&_p]:leading-6 [&_p]:mb-4 [&_p]:text-gray-700 [&_p]:text-sm [&_p]:text-justify
											[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-2 [&_ul]:mb-4
											[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-2 [&_ol]:mb-4
											[&_li]:text-gray-700 [&_li]:text-sm [&_li]:leading-6
											[&_strong]:font-semibold [&_strong]:text-gray-800
											[&_em]:italic
											[&_br]:mb-2" containerStyle={{
                  marginTop: 0,
                  textAlign: "left",
                  lineHeight: "1.6",
                  fontSize: "14px"
                }} />
										</div>
										<Checkbox isSelected={watchedValues.agreedToTerms} onChange={handleAgree}>
											I Agree
										</Checkbox>
									</SheetContent>
								</SheetOverlay> : <DialogOverlay isOpen={isSheetOpen} onOpenChange={handleSheetOpenChange}>
									<DialogContent className="max-w-[70vw] max-h-[90vh] overflow-y-auto" closeButton={true}>
										<div className="flex flex-col gap-1.5 mb-4">
											<h2 className="text-xl font-semibold text-gray-900">MOU</h2>
										</div>

										{/* MOU Content - Scrollable */}
										<div className="flex-1 overflow-y-auto">
											<EmailOrSms params={currentTemplateParams} query={query} showSubject={false} showSignature={false} className="prose prose-sm max-w-none 
											[&_p]:leading-6 [&_p]:mb-4 [&_p]:text-gray-700 [&_p]:text-sm [&_p]:text-justify
											[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-2 [&_ul]:mb-4
											[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-2 [&_ol]:mb-4
											[&_li]:text-gray-700 [&_li]:text-sm [&_li]:leading-6
											[&_strong]:font-semibold [&_strong]:text-gray-800
											[&_em]:italic
											[&_br]:mb-2" containerStyle={{
                  marginTop: 0,
                  textAlign: "left",
                  lineHeight: "1.6",
                  fontSize: "14px"
                }} />
										</div>
										<Checkbox isSelected={watchedValues.agreedToTerms} onChange={handleAgree}>
											I Agree
										</Checkbox>
									</DialogContent>
								</DialogOverlay>}

							<div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
								{/* Review MOU */}
								<Button variant={isMobile ? "outline" : "ghost"} onPress={() => setIsSheetOpen(true)} className="w-full py-3">
									Review MOU
								</Button>

								{/* I Agree Button */}
								<Button variant={watchedValues.agreedToTerms ? "outline" : "default"} onPress={handleAgree} className="w-full py-3">
									I Agree
								</Button>
							</div>

							{/* Email Verification */}
							{watchedValues.agreedToTerms && <>
									{!isEmailVerified && <div className="flex items-start sm:items-center space-x-2 bg-amber-50 border border-amber-200 rounded-lg p-2 sm:p-3">
											<svg className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 flex-shrink-0 mt-0.5 sm:mt-0" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
												<path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
											</svg>
											<p className="text-amber-800 text-xs sm:text-sm">Please verify your email address to proceed.</p>
										</div>}
									<EmailVerificationDialog isShort required defaultStep={isEmailVerified ? "verified" : "email"} onVerificationSuccess={handleEmailVerificationSuccess} defaultEmail={watchedValues.email || ""} />
								</>}

							{/* Bottom Text */}
							<div className="bg-purple-50 border border-purple-200 rounded-lg p-2 sm:p-3">
								<p className="text-purple-800 text-xs sm:text-sm text-center">
									We will email you a copy of the agreement upon confirmation.
								</p>
							</div>
						</form>
					</FormProvider>
				</> : <>
					{/* Success Message after unit confirmation */}
					<div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl shadow-sm mb-6 p-6 transition-all duration-300 hover:shadow-md">
						<div className="flex items-start space-x-3 mb-4">
							<div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
								<svg className="w-5 h-5 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
									<path d="M5 13l4 4L19 7" />
								</svg>
							</div>
							<div>
								<h2 className="text-xl font-semibold text-green-700">Unit Confirmed Successfully!</h2>
							</div>
						</div>

						<p className="text-gray-700 mb-4 leading-relaxed">
							{"Thank you for confirming your unit details. Here's what happens next:"}
						</p>

						<ul className="space-y-3">
							<li className="flex items-start space-x-3 text-gray-700 group">
								<span className="flex-shrink-0 w-1.5 h-1.5 bg-green-500 rounded-full mt-2 group-hover:scale-125 transition-transform" aria-hidden="true" />
								<span className="leading-relaxed">Our executive will visit for in-person verification.</span>
							</li>
							<li className="flex items-start space-x-3 text-gray-700 group">
								<span className="flex-shrink-0 w-1.5 h-1.5 bg-green-500 rounded-full mt-2 group-hover:scale-125 transition-transform" aria-hidden="true" />
								<span className="leading-relaxed">
									We will proceed with floorplan creation and quality photography.
								</span>
							</li>
							<li className="flex items-start space-x-3 text-gray-700 group">
								<span className="flex-shrink-0 w-1.5 h-1.5 bg-green-500 rounded-full mt-2 group-hover:scale-125 transition-transform" aria-hidden="true" />
								<span className="leading-relaxed">A dedicated relationship manager will be assigned to you.</span>
							</li>
						</ul>
					</div>
					<ButtonActions next onNext={onNext} onBack={onBack} isLoading={isLoading} backTitle="Unit details feature coming soon" disableBack={true} disabled={!watchedValues.isConfirmationDone && (!isEmailVerified || !watchedValues.agreedToTerms || isSubmitting)} />
				</>}
		</div>;
}